import {
  AccountProductEnum,
  BannerMessageR2Type,
  InlineAlertType,
  ListTile,
  ListTileType,
  NotificationBannerTile,
  SlComponent,
  User,
} from '@varo/graphql-schema-types';

export type ScrollEvent = {
  layoutMeasurement: {
    height: number;
  };
  contentOffset: {
    y: number;
  };
  contentSize: {
    height: number;
  };
};

export interface TransactionsProps {
  accountId: string;
  pagingToken?: string;
  categoryId?: string | null | undefined;
  cachedTransactions?: SlComponent[];
  fetchTransactions: boolean;
  accountType?: string;
}

export interface UserInfo {
  user: User;
}

/*
 * merges SL components (months) based on 'id'
 *   - if the same 'id' is found txs will be concatenated
 *   - if there is no 'id' match new month will be appended
 */
export const getMergedSmartLedgerComponents = (
  currentComponents: SlComponent[],
  newComponents?: SlComponent[],
) => {
  const mergedComponents = [...currentComponents];

  newComponents?.forEach((group) => {
    // copy the group object we receive from GQL
    const groupCopy = Object.assign({}, group);

    const childrenCopy = [...groupCopy.children];
    groupCopy.children = childrenCopy;

    const currentMatch = mergedComponents?.find(
      (cur) => cur.id === groupCopy.id,
    );

    // if we already have this group we need to merge the children
    if (currentMatch) {
      currentMatch.children = [...currentMatch.children, ...groupCopy.children];
    } else {
      // else we can just add it unadjusted
      mergedComponents?.push(groupCopy);
    }
  });

  return mergedComponents;
};

export const isPositiveValue = (value: string) => {
  return value.includes('-$') ? 'NEGATIVE' : 'POSITIVE';
};

export const getSourceName = (source: string | null, isCategory = false) => {
  switch (source) {
    case 'ALL_TRANSACTIONS':
      return isCategory ? 'all' : 'ledger_all_transactions_page';

    case 'EXPENSE':
      return isCategory ? 'debit' : 'ledger_money_out_page';

    case 'INCOME':
      return isCategory ? 'credit' : 'ledger_money_in_page';

    case 'VARO_BELIEVE':
      return 'account_page';

    default:
      return '';
  }
};

export const getBannerData = (tiles) => {
  return (tiles as ListTile[])?.find(
    (item) => item?.listTileType === ListTileType?.Banners,
  )?.tiles[0] as NotificationBannerTile;
};

export const getAlertType = (alertType: string) => {
  switch (alertType) {
    case 'INFO':
      return InlineAlertType?.Info;
    case 'POSITIVE':
      return InlineAlertType?.Success;
    case 'NEGATIVE':
      return InlineAlertType?.Error;
    default:
      return InlineAlertType?.Warning;
  }
};

export const mapAlertType = (type: BannerMessageR2Type): InlineAlertType => {
  switch (type) {
    case BannerMessageR2Type.Error:
      return InlineAlertType.Error;
    case BannerMessageR2Type.Warning:
      return InlineAlertType.Warning;
    case BannerMessageR2Type.Success:
      return InlineAlertType.Success;
    default:
      return InlineAlertType.Info;
  }
};

export const getAccountAttributes = (
  selectedTab: string | undefined,
  product: AccountProductEnum | undefined,
) => {
  if (product) {
    if (product === AccountProductEnum.Advance) {
      return undefined;
    }

    const productChargeCard =
      product === AccountProductEnum.ChargeCard
        ? 'VARO_BELIEVE'
        : (selectedTab as string);
    const productSavings =
      product === AccountProductEnum.Savings &&
      `${product?.toLowerCase()}_account`;

    const sourceName = selectedTab
      ? (getSourceName(productChargeCard) as string)
      : productSavings;

    return {
      source_name: sourceName,
      account_type: product.toLowerCase() as string,
      category:
        product === AccountProductEnum.ChargeCard
          ? 'varo_believe'
          : 'account_management',
      ...(product === AccountProductEnum.ChargeCard && {
        subcategory: 'believe_management',
      }),
    };
  }
  return undefined;
};
